import type { ComboboxOption } from '../../ui/v2';

export type AgentSortKey =
  | 'store_name'
  | 'assistant_id'
  | 'assistant_provider'
  | 'phone_number'
  | 'language'
  | 'retell_llm_model'
  | 'is_active';

export const AGENT_SORT_KEY_TO_FIELD: Record<AgentSortKey, string> = {
  store_name: 'store_name',
  assistant_id: 'assistant_id',
  assistant_provider: 'assistant_provider',
  phone_number: 'phone_number',
  language: 'language',
  retell_llm_model: 'retell_llm_model',
  is_active: 'is_active',
};

export interface AdminAssistant {
  id?: number;
  store_id?: number | null;
  store_name?: string | null;
  store?: { id?: number } | null;
  assistant_id?: string | null;
  assistant_provider?: string | null;
  phone_number?: string | null;
  retell_voice_id?: string | null;
  retell_llm_model?: string | null;
  language?: string | null;
  is_active?: boolean;
}

export interface AgentFormData {
  store: number;
  assistant_id: string;
  assistant_provider: string;
  phone_number: string;
  retell_voice_id: string;
  retell_llm_model: string;
  language: string;
  is_active: boolean;
}

export interface PickerOption {
  value: string | number;
  label: string;
}

export const EMPTY_AGENT_FORM: AgentFormData = {
  store: 0,
  assistant_id: '',
  assistant_provider: 'retell',
  phone_number: '',
  retell_voice_id: '',
  retell_llm_model: '',
  language: 'en',
  is_active: true,
};

export const agentFormFrom = (assistant: AdminAssistant): AgentFormData => ({
  store: assistant.store_id || assistant.store?.id || 0,
  assistant_id: assistant.assistant_id || '',
  assistant_provider: assistant.assistant_provider || 'retell',
  phone_number: assistant.phone_number || '',
  retell_voice_id: assistant.retell_voice_id || '',
  retell_llm_model: assistant.retell_llm_model || '',
  language: assistant.language || 'en',
  is_active: assistant.is_active !== undefined ? assistant.is_active : true,
});

export const updateAgentForm = (
  form: AgentFormData,
  target: { name: string; value: string; type?: string; checked?: boolean },
): AgentFormData => ({
  ...form,
  [target.name]:
    target.type === 'checkbox'
      ? Boolean(target.checked)
      : target.name === 'store'
        ? target.value === ''
          ? 0
          : parseInt(target.value, 10) || 0
        : target.value,
});

export const validateAgentForm = (form: AgentFormData): string | null => {
  if (!form.store) return 'Please select a store';
  if (!form.assistant_id.trim()) return 'Agent ID is required';
  return null;
};

export const agentPayload = (form: AgentFormData) => ({
  store: form.store,
  assistant_id: form.assistant_id.trim(),
  assistant_provider: form.assistant_provider.trim(),
  phone_number: form.phone_number.trim() || null,
  retell_voice_id: form.retell_voice_id.trim() || null,
  retell_llm_model: form.retell_llm_model.trim() || null,
  language: form.language.trim(),
  is_active: form.is_active,
});

export const agentStoreLabel = (assistant: AdminAssistant): string =>
  assistant.store_name && assistant.store_id
    ? `${assistant.store_name} (ID: ${assistant.store_id})`
    : assistant.store_id
      ? `Store ID: ${assistant.store_id}`
      : '—';

export const toComboboxOptions = (options: PickerOption[]): ComboboxOption[] =>
  options.map((option) => ({
    value: String(option.value),
    label: option.label,
  }));

/**
 * An optional picker can be cleared back to nothing by choosing its
 * placeholder, which is listed first once something is selected.
 */
export const withClearOption = (
  options: ComboboxOption[],
  value: string,
  placeholder: string,
): ComboboxOption[] =>
  value ? [{ value: '', label: placeholder }, ...options] : options;

type AgentAction = 'create' | 'update';

export const agentResponseError = async (
  response: Response | null | undefined,
  action: AgentAction,
): Promise<string> => {
  if (!response) return `Failed to ${action} agent: No response from server`;
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    try {
      const data = await response.json();
      return (
        data.detail ||
        data.message ||
        data.error ||
        `Failed to ${action} agent (Status: ${response.status})`
      );
    } catch {
      return `Failed to ${action} agent (Status: ${response.status}). Could not parse error response.`;
    }
  }
  const text = await response.text();
  console.error('Non-JSON response:', text.substring(0, 200));
  return `Failed to ${action} agent (Status: ${response.status}). The server returned an error page.`;
};

export const agentThrownError = (err: unknown): string => {
  if (err instanceof Error) {
    if (err.message.includes('JSON') || err.message.includes('DOCTYPE')) {
      return 'The server returned an unexpected response. Please check that the API endpoint is correct and the server is running.';
    }
    return err.message;
  }
  if (typeof err === 'string') return err;
  return 'An unexpected error occurred. Please try again.';
};
